import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Shield, Truck, Award, Star, CheckCircle, ChevronRight, Package, Users, TrendingUp, Leaf, X, ZoomIn, Calendar, BookOpen } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { AnimatedSection, SectionLabel, Seo } from "../components/ui/index";
import { getImageUrl } from "../utils/image";

function safeParseJson(str, defaultValue) {
  if (!str) return defaultValue;
  let clean = str.replace(/<[^>]*>/g, "");
  clean = clean
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
  try {
    return JSON.parse(clean);
  } catch (e) {
    console.error("Failed to parse JSON setting:", clean, e);
    return defaultValue;
  }
}

const getIconComponent = (iconName) => {
  if (!iconName) return LucideIcons.HelpCircle;
  const formatted = iconName.charAt(0).toUpperCase() + iconName.slice(1);
  return LucideIcons[formatted] || LucideIcons[iconName] || LucideIcons.HelpCircle;
};
import { useSettings } from "../context/SettingsContext";
import { productsAPI, servicesAPI, cmsAPI, galleryAPI, certificationsAPI, brandsAPI, blogAPI } from "../services/api";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

function StatBox({ value, suffix, label, icon: Icon }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <div ref={ref} className="text-center p-6 bg-white/10 rounded-2xl backdrop-blur-sm">
      <Icon className="w-7 h-7 text-accent-gold mx-auto mb-3" />
      <div className="font-heading text-4xl font-extrabold text-white mb-1">
        {inView ? <CountUp end={value} duration={2.5} suffix={suffix} separator="," /> : "0"}
      </div>
      <div className="text-brand-muted text-sm font-medium">{label}</div>
    </div>
  );
}

export default function HomePage() {
  const { get } = useSettings();
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [cms, setCms] = useState({});
  const [gallery, setGallery] = useState([]);
  const [certs, setCerts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [viewGalleryItem, setViewGalleryItem] = useState(null);
  const [viewCertItem, setViewCertItem] = useState(null);

  useEffect(() => {
    productsAPI.getAll({ featured: true, limit: 6 }).then(r => setProducts(r.data.products || [])).catch(() => {});
    servicesAPI.getAll().then(r => setServices((r.data.services || []).slice(0, 3))).catch(() => {});
    cmsAPI.getPage("home").then(r => setCms(r.data.content || {})).catch(() => {});
    galleryAPI.getAll({ featured: true }).then(r => setGallery(r.data.items || [])).catch(() => {});
    certificationsAPI.getAll().then(r => setCerts(r.data.certifications || [])).catch(() => {});
    brandsAPI.getAll().then(r => setBrands(r.data.brands || [])).catch(() => {});
    blogAPI.getRecent().then(r => setBlogs(r.data.posts || [])).catch(() => {});
  }, []);

  const heroHeading = cms?.hero?.heading?.value || "Premium Quality Food Trading Worldwide";
  const heroDesc = cms?.hero?.description?.value || "Sava Fresh Trading FZE connects the world's finest agricultural producers with global markets. Trusted sourcing, certified quality, worldwide delivery.";
  const heroCtaPrimary = cms?.hero?.cta_primary?.value || "Explore Products";
  const heroCtaSecondary = cms?.hero?.cta_secondary?.value || "Get in Touch";

  const aboutHeading = cms?.about?.heading?.value || "Global Trading Excellence";
  const aboutDesc = cms?.about?.description?.value || "With deep expertise in international food trade, Sava Fresh bridges quality producers and global buyers with unmatched reliability.";

  const rawStatsJson = cms?.stats?.stats_json?.value;
  const parsedStats = rawStatsJson ? safeParseJson(rawStatsJson, null) : null;
  const statsList = parsedStats || [
    { value: 50, suffix: "+", label: "Countries Served", icon: "Globe" },
    { value: 500, suffix: "+", label: "Products Sourced", icon: "Package" },
    { value: 200, suffix: "+", label: "Global Clients", icon: "Users" },
    { value: 15, suffix: "+", label: "Years Experience", icon: "TrendingUp" },
  ];
  const mappedStats = statsList.map(s => ({
    ...s,
    icon: getIconComponent(s.icon)
  }));

  const rawAdvantagesJson = cms?.advantages?.advantages_json?.value;
  const parsedAdvantages = rawAdvantagesJson ? safeParseJson(rawAdvantagesJson, null) : null;
  const advantagesList = parsedAdvantages || [
    { icon: "Shield", title: "Certified Quality", desc: "Every product meets international food safety and quality standards with full traceability.", color: "brand" },
    { icon: "Globe", title: "Global Network", desc: "Direct relationships with producers across Asia, Africa, Europe, and the Americas.", color: "gold" },
    { icon: "Truck", title: "Reliable Logistics", desc: "End-to-end supply chain management with temperature-controlled shipping worldwide.", color: "brand" },
    { icon: "Award", title: "Trusted Partner", desc: "Over 15 years of trusted partnerships with buyers, retailers, and food manufacturers.", color: "gold" },
    { icon: "Leaf", title: "Fresh & Natural", desc: "Committed to sourcing the freshest, most natural products with minimal processing.", color: "brand" },
    { icon: "CheckCircle", title: "Full Compliance", desc: "Complete regulatory compliance for import/export across all major global markets.", color: "gold" },
  ];
  const mappedAdvantages = advantagesList.map(f => ({
    ...f,
    icon: getIconComponent(f.icon)
  }));

  return (
    <div className="overflow-hidden">
      <Seo page="home" />
      {/* ── HERO ─────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center bg-white pt-20">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-pale rounded-full opacity-50" />
          <div className="absolute top-1/3 -right-20 w-64 h-64 bg-accent-goldLight rounded-full opacity-30" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-brand-pale rounded-full opacity-40" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center py-16">
            {/* Left */}
            <AnimatedSection direction="left">
              <div className="badge-brand mb-6">
                <Globe className="w-3 h-3" />
                Global Food Trading Excellence
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-[40px] xl:text-[42px] font-extrabold text-gray-900 leading-tight mb-6">
                {heroHeading.split(" ").slice(0, 3).join(" ")}{" "}
                <span style={{ background: "linear-gradient(135deg, #113339 0%, #2a8a96 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  {heroHeading.split(" ").slice(3, 5).join(" ")}
                </span>{" "}
                {heroHeading.split(" ").slice(5).join(" ")}
              </h1>
              <div className="text-gray-500 text-lg leading-relaxed mb-8 max-w-lg rich-content" dangerouslySetInnerHTML={{ __html: heroDesc }} />
              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/products" className="btn-primary px-8 py-4">
                  {heroCtaPrimary} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="btn-outline px-8 py-4">{heroCtaSecondary}</Link>
              </div>
              {/* Trust marks */}
              <div className="flex flex-wrap gap-4">
                {["ISO Certified", "HACCP Compliant", "Export Licensed"].map(t => (
                  <div key={t} className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
                    <CheckCircle className="w-4 h-4 text-brand-primary" />
                    <span className="text-xs font-semibold text-gray-700">{t}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Right - Visual (Video or Image) */}
            <AnimatedSection direction="right" delay={200}>
              <div className="relative rounded-3xl overflow-hidden shadow-hover border-4 border-white bg-black group aspect-[4/3] lg:aspect-[1.2]">
                {cms?.hero?.video_enabled?.value === "true" && cms?.hero?.video_url?.value ? (
                  <video
                    src={getImageUrl(cms.hero.video_url.value)}
                    poster={getImageUrl(cms?.hero?.video_poster?.value || cms?.hero?.imageUrl?.value || "")}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={getImageUrl(cms?.hero?.imageUrl?.value || "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80")}
                    alt="Sava Fresh Hero"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── STATS ───────────────────────────────────── */}
      <section className="bg-brand-primary py-16">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {mappedStats.map((s, i) => (
              <AnimatedSection key={s.label} delay={i * 100}>
                <StatBox {...s} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT STRIP ─────────────────────────────── */}
      <section className="section-pad">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection direction="left">
              <SectionLabel text="About Sava Fresh" />
              <h2 className="section-heading mb-6">{aboutHeading}</h2>
              {aboutDesc.trim().startsWith("<") ? (
                <div className="text-gray-600 leading-relaxed mb-6 wysiwyg-output" dangerouslySetInnerHTML={{ __html: aboutDesc }} />
              ) : (
                <p className="text-gray-600 leading-relaxed mb-6">{aboutDesc}</p>
              )}
              <ul className="space-y-3 mb-8">
                {[
                  cms?.about?.point1?.value || "Direct relationships with 200+ certified producers worldwide",
                  cms?.about?.point2?.value || "Full cold-chain logistics and quality control at every step",
                  cms?.about?.point3?.value || "Regulatory compliance for all major import/export markets",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                    <CheckCircle className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about" className="btn-primary">
                Learn More About Us <ChevronRight className="w-4 h-4" />
              </Link>
            </AnimatedSection>
            <AnimatedSection direction="right" delay={200}>
              <div className="relative rounded-3xl overflow-hidden shadow-hover border-4 border-white bg-white group aspect-[4/3] lg:aspect-[1.2]">
                <img
                  src={getImageUrl(cms?.about?.imageUrl?.value || "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=800&q=80")}
                  alt="About Sava Fresh"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── SERVICES ────────────────────────────────── */}
      {services.length > 0 && (
        <section className="section-pad bg-gray-section">
          <div className="container-custom">
            <AnimatedSection className="text-center mb-12">
              <SectionLabel text="Our Services" />
              <h2 className="section-heading mb-4">What We Offer</h2>
              <p className="section-subheading mx-auto">End-to-end trading solutions for food businesses worldwide.</p>
            </AnimatedSection>
            <div className="grid md:grid-cols-3 gap-6">
              {services.map((svc, i) => (
                <AnimatedSection key={svc.id} delay={i * 100}>
                  <div className="bg-white rounded-2xl p-6 shadow-card hover:shadow-hover transition-all duration-300 border border-transparent hover:border-brand-muted group">
                    {svc.imageUrl && <img src={svc.imageUrl} alt={svc.title} className="w-12 h-12 object-contain mb-4" onError={e => e.target.style.display="none"} />}
                    {!svc.imageUrl && <div className="w-12 h-12 bg-brand-pale rounded-xl flex items-center justify-center mb-4"><Package className="w-6 h-6 text-brand-primary" /></div>}
                    <h3 className="font-heading font-bold text-gray-900 mb-2">{svc.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">{svc.excerpt || svc.description?.replace(/<[^>]+>/g, "").slice(0, 100) + "..."}</p>
                    <Link to={`/services/${svc.slug || svc.id}`} className="text-brand-primary text-sm font-semibold hover:underline flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </AnimatedSection>
              ))}
            </div>
            <AnimatedSection className="text-center mt-10">
              <Link to="/services" className="btn-outline">View All Services <ChevronRight className="w-4 h-4" /></Link>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* ── WHY CHOOSE US ───────────────────────────── */}
      <section className="section-pad">
        <div className="container-custom">
          <AnimatedSection className="text-center mb-16">
            <SectionLabel text="Why Sava Fresh" />
            <h2 className="section-heading mb-4">The Sava Fresh Advantage</h2>
            <p className="section-subheading mx-auto">Built on trust, quality, and a global network — we deliver more than products.</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mappedAdvantages.map((f, i) => (
              <AnimatedSection key={f.title} delay={i * 80}>
                <div className="group p-6 rounded-2xl border border-gray-100 hover:border-brand-muted hover:shadow-hover transition-all duration-300 bg-white">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${f.color === "gold" ? "bg-accent-goldLight group-hover:bg-accent-gold/20" : "bg-brand-pale group-hover:bg-brand-muted"}`}>
                    <f.icon className={`w-6 h-6 ${f.color === "gold" ? "text-accent-gold" : "text-brand-primary"}`} />
                  </div>
                  <h3 className="font-heading font-semibold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ───────────────────────── */}
      {products.length > 0 && (
        <section className="section-pad bg-gray-section">
          <div className="container-custom">
            <AnimatedSection className="flex items-start sm:items-center justify-between mb-12 gap-4 flex-col sm:flex-row">
              <div>
                <SectionLabel text="Our Products" />
                <h2 className="section-heading">Featured Products</h2>
              </div>
              <Link to="/products" className="btn-ghost whitespace-nowrap">View All <ArrowRight className="w-4 h-4" /></Link>
            </AnimatedSection>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, i) => (
                <AnimatedSection key={product.id} delay={i * 80}>
                  <Link to={`/products/${product.slug}`} className="group bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden block hover:-translate-y-1">
                    <div className="h-48 bg-brand-pale overflow-hidden">
                      {product.imageUrl ? (
                        <img src={getImageUrl(product.imageUrl)} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={e => e.target.parentElement.classList.add("flex","items-center","justify-center")} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-16 h-16 text-brand-muted" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <span className="badge-brand mb-2">{product.category}</span>
                      <h3 className="font-heading font-bold text-gray-900 mt-2 mb-1">{product.name}</h3>
                      <p className="text-gray-500 text-sm line-clamp-2">{product.shortDesc || product.description?.replace(/<[^>]+>/g, "").slice(0, 80)}</p>
                      <div className="mt-4 flex items-center gap-2 text-brand-primary text-sm font-semibold group-hover:gap-3 transition-all">
                        View Details <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── OUR BRANDS ───────────────────────────────── */}
      {brands.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white border-y border-gray-100">
          <div className="container-custom">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <SectionLabel text="Trusted Portfolio" color="gold" />
              <h2 className="section-heading mb-3">Our Leading Brands</h2>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed">
                We partner with world-renowned food producers and market leading brands to supply premium foodstuff across global markets.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 items-center">
              {brands.map((b, i) => (
                <AnimatedSection key={b.id} delay={i * 40}>
                  <a
                    href={b.website || "#"}
                    target={b.website ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                    className="bg-white p-6 rounded-2xl border border-gray-100 shadow-soft hover:shadow-hover hover:border-brand-primary/30 transition-all duration-300 flex items-center justify-center h-28 group"
                    title={b.name}
                  >
                    <img
                      src={getImageUrl(b.logoUrl)}
                      alt={b.name}
                      className="max-h-14 max-w-full object-contain filter grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-300"
                    />
                  </a>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── GALLERY PREVIEW ─────────────────────────── */}
      {gallery.length > 0 && (
        <section className="section-pad">
          <div className="container-custom">
            <AnimatedSection className="flex items-start sm:items-center justify-between mb-12 gap-4 flex-col sm:flex-row">
              <div>
                <SectionLabel text="Our Gallery" />
                <h2 className="section-heading">Featured Highlights</h2>
              </div>
              <Link to="/gallery" className="btn-ghost whitespace-nowrap">View Full Gallery <ArrowRight className="w-4 h-4" /></Link>
            </AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.slice(0, 8).map((item, i) => (
                <AnimatedSection key={item.id} delay={i * 40}>
                  <div
                    className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-card hover:shadow-hover transition-all duration-300"
                    style={{ minHeight: "160px" }}
                    onClick={() => setViewGalleryItem(item)}
                  >
                    <img
                      src={getImageUrl(item.imageUrl)}
                      alt={item.altText || item.title}
                      className="w-full h-44 object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={e => e.target.parentElement.classList.add("bg-brand-pale", "flex", "items-center", "justify-center")}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <span className="badge-brand mb-1 text-[10px] inline-block">{item.category}</span>
                      <h4 className="font-semibold text-white text-xs">{item.title}</h4>
                    </div>
                    <div className="absolute top-2.5 right-2.5 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CERTIFICATIONS SHOWCASE ─────────────────── */}
      {certs.length > 0 && (
        <section className="section-pad bg-gray-section">
          <div className="container-custom">
            <AnimatedSection className="text-center mb-12">
              <SectionLabel text="Certifications" />
              <h2 className="section-heading">Quality & Compliance</h2>
              <p className="section-subheading mx-auto">
                We maintain the highest standards of food safety and quality control, backed by international certifications.
              </p>
            </AnimatedSection>
            
            {/* Certifications Logo Grid */}
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {certs.filter(c => c.featured).map((cert, i) => (
                <AnimatedSection key={cert.id} delay={i * 80}>
                  <div
                    onClick={() => setViewCertItem(cert)}
                    className="bg-white rounded-2xl p-4 shadow-card hover:shadow-hover hover:-translate-y-1 transition-all duration-300 border border-gray-100 flex flex-col items-center justify-center cursor-pointer w-36 h-36 group"
                  >
                    {cert.logoUrl ? (
                      <img
                        src={getImageUrl(cert.logoUrl)}
                        alt={cert.name}
                        className="w-20 h-20 object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <span className="text-4xl">🏆</span>
                    )}
                    <span className="text-[10px] text-gray-500 font-bold mt-2 text-center line-clamp-1">{cert.name}</span>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── LATEST BLOGS ─────────────────────────────── */}
      {blogs.length > 0 && (
        <section className="section-pad bg-brand-pale/30">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div>
                <SectionLabel text="Market Intelligence" />
                <h2 className="section-heading">Latest Industry News & Insights</h2>
                <p className="section-subheading mt-2">
                  Stay updated with global food trading trends, market shifts, and agricultural supply chain updates.
                </p>
              </div>
              <Link to="/blog" className="btn-outline self-start md:self-auto">
                View All Articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {blogs.map((b, i) => (
                <AnimatedSection key={b.id || b.slug} delay={i * 80}>
                  <article className="card flex flex-col h-full group border border-gray-100 hover:border-brand-muted">
                    <div className="h-48 bg-brand-pale overflow-hidden relative flex-shrink-0">
                      {b.imageUrl ? (
                        <img
                          src={getImageUrl(b.imageUrl)}
                          alt={b.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-brand-muted" />
                        </div>
                      )}
                      {b.category && <div className="absolute top-3 left-3 badge-brand text-xs shadow-sm">{b.category}</div>}
                    </div>
                    <div className="p-6 flex flex-col flex-1 justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{b.publishedAt ? new Date(b.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</span>
                        </div>
                        <h3 className="font-heading font-bold text-gray-900 text-lg mb-2 group-hover:text-brand-primary transition-colors line-clamp-2 leading-snug">
                          <Link to={`/blog/${b.slug}`}>{b.title}</Link>
                        </h3>
                        {b.excerpt && <p className="text-gray-500 text-sm line-clamp-2 leading-relaxed mb-4">{b.excerpt}</p>}
                      </div>
                      <Link to={`/blog/${b.slug}`} className="inline-flex items-center gap-1.5 text-brand-primary text-xs font-bold hover:underline mt-auto">
                        Read Article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </article>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ─────────────────────────────────────── */}
      <section className="py-20 bg-brand-primary">
        <div className="container-custom text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              {(() => {
                const ctaHeading = cms?.cta?.heading?.value || "Ready to Source Globally?";
                return ctaHeading;
              })()}
            </h2>
            {(() => {
              const ctaDesc = cms?.cta?.description?.value || "Join 200+ businesses worldwide who trust Sava Fresh for their food sourcing needs.";
              return ctaDesc.trim().startsWith("<") ? (
                <div className="text-brand-muted text-lg mb-8 max-w-xl mx-auto wysiwyg-output" dangerouslySetInnerHTML={{ __html: ctaDesc }} />
              ) : (
                <p className="text-brand-muted text-lg mb-8 max-w-xl mx-auto">
                  {ctaDesc}
                </p>
              );
            })()}
            {(() => {
              const ctaBtnPrimaryTxt = cms?.cta?.btn_primary_txt?.value || "Send Enquiry";
              const ctaBtnPrimaryLink = cms?.cta?.btn_primary_link?.value || "/contact";
              const ctaBtnSecondaryTxt = cms?.cta?.btn_secondary_txt?.value || "Browse Products";
              const ctaBtnSecondaryLink = cms?.cta?.btn_secondary_link?.value || "/products";
              return (
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link to={ctaBtnPrimaryLink} className="bg-white text-brand-primary px-8 py-4 rounded-xl font-semibold hover:bg-brand-pale transition-all duration-300 flex items-center gap-2 shadow-soft">
                    {ctaBtnPrimaryTxt} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to={ctaBtnSecondaryLink} className="border-2 border-white/40 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 flex items-center gap-2">
                    {ctaBtnSecondaryTxt} <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })()}
          </AnimatedSection>
        </div>
      </section>

      {/* ── MODALS / LIGHTBOXES ─────────────────────── */}
      {viewGalleryItem && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in" onClick={() => setViewGalleryItem(null)}>
          <div className="max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <img src={getImageUrl(viewGalleryItem.imageUrl)} alt={viewGalleryItem.altText || viewGalleryItem.title} className="w-full max-h-[70vh] object-contain rounded-2xl" />
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl mt-3 p-4 flex items-center justify-between">
              <div>
                <p className="text-white font-semibold">{viewGalleryItem.title}</p>
                <span className="text-white/60 text-xs">{viewGalleryItem.category}</span>
              </div>
              <button onClick={() => setViewGalleryItem(null)} className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {viewCertItem && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setViewCertItem(null)}>
          <div className="max-w-xl w-full bg-white rounded-3xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="relative p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading font-bold text-gray-900 text-lg">{viewCertItem.name}</h3>
              <button onClick={() => setViewCertItem(null)} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              {viewCertItem.imageUrl ? (
                <img src={getImageUrl(viewCertItem.imageUrl)} alt={viewCertItem.name} className="w-full h-auto max-h-96 object-contain rounded-2xl border border-gray-100 p-2 bg-gray-50" />
              ) : (
                viewCertItem.logoUrl && (
                  <img src={getImageUrl(viewCertItem.logoUrl)} alt={viewCertItem.name} className="w-48 h-48 mx-auto object-contain p-4 bg-gray-50 rounded-2xl" />
                )
              )}
              {viewCertItem.description && (
                <div className="text-gray-600 text-sm leading-relaxed bg-gray-50 p-4 rounded-2xl rich-content" dangerouslySetInnerHTML={{ __html: viewCertItem.description }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
